import React, { useEffect, useRef, useState } from 'react';
import { Participant, RemoteVideoTrack, RemoteAudioTrack } from 'twilio-video';
import InitialsAvatar from 'components/messaging/components/initials-avatar';

interface RemoteParticipantProps {
  participant: Participant;
}

const RemoteParticipant: React.FC<RemoteParticipantProps> = ({ participant }) => {
  const [videoTracks, setVideoTracks] = useState<RemoteVideoTrack[]>([]);
  const [audioTracks, setAudioTracks] = useState<RemoteAudioTrack[]>([]);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);

  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const trackpubsToTracksVideo = (trackMap: any) =>
    Array.from(trackMap.values())
      .map((pub: any) => pub.track)
      .filter((track: any) => track !== null);

  const trackpubsToTracksAudio = (trackMap: any) =>
    Array.from(trackMap.values())
      .map((pub: any) => pub.track)
      .filter((track: any) => track !== null);

  useEffect(() => {
    setVideoTracks(trackpubsToTracksVideo(participant.videoTracks));
    setAudioTracks(trackpubsToTracksAudio(participant.audioTracks));

    const handleTrackSubscribed = (track: any) => {
      if (track.kind === 'video') setVideoTracks((prev) => [...prev, track]);
      if (track.kind === 'audio') setAudioTracks((prev) => [...prev, track]);
    };

    const handleTrackUnsubscribed = (track: any) => {
      if (track.kind === 'video')
        setVideoTracks((prev) => prev.filter((v) => v !== track));
      if (track.kind === 'audio')
        setAudioTracks((prev) => prev.filter((a) => a !== track));
    };

    const handleTrackEnabled = (track: any) => {
      if (track.kind === 'video') setIsVideoEnabled(true);
    };
    const handleTrackDisabled = (track: any) => {
      if (track.kind === 'video') setIsVideoEnabled(false);
    };

    participant.on('trackSubscribed', handleTrackSubscribed);
    participant.on('trackUnsubscribed', handleTrackUnsubscribed);
    participant.on('trackEnabled', handleTrackEnabled);
    participant.on('trackDisabled', handleTrackDisabled);

    return () => {
      participant.removeAllListeners();
    };
  }, [participant]);

  useEffect(() => {
    const videoTrack = videoTracks[videoTracks.length - 1];
    if (videoRef.current) {
      // Detach all tracks first
      videoTracks.forEach((track) => {
        try {
          track.detach();
        } catch (e) {}
      });
      // Attach only the primary (latest) track
      if (videoTrack) videoTrack.attach(videoRef.current);
    }
    return () => {
      if (videoRef.current) {
        videoTracks.forEach((track) => {
          try {
            track.detach();
          } catch (e) {}
        });
      }
    };
  }, [videoTracks]);

  useEffect(() => {
    const audioTrack = audioTracks[audioTracks.length - 1];
    if (audioRef.current) {
      // Detach all tracks first
      audioTracks.forEach((track) => {
        try {
          track.detach();
        } catch (e) {}
      });
      // Attach only the primary (latest) track
      if (audioTrack) audioTrack.attach(audioRef.current);
    }
    return () => {
      if (audioRef.current) {
        audioTracks.forEach((track) => {
          try {
            track.detach();
          } catch (e) {}
        });
      }
    };
  }, [audioTracks]);

  const showVideo = videoTracks.length > 0 && isVideoEnabled;
  const userInfo = JSON.parse(participant.identity);

  return (
    <div style={{ width: '100%', height: '100%', background: '#1f1f1f' }}>
      {showVideo ? (
        <video
          ref={videoRef}
          autoPlay
          playsInline
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <InitialsAvatar
          name={userInfo.username?.replace(/[0-9]/g, '')}
          maxInitials={2}
          textSizeRatio={2}
          size="300"
          round
        />
      )}
      <audio ref={audioRef} autoPlay />
    </div>
  );
};

export default RemoteParticipant;